'use client'

import { BottomSheet, Button, Cell, Divider, Icon, Label, Spacer } from '@vinted/web-ui'
import type { ButtonProps } from '@vinted/web-ui'
import { HorizontalDots24 } from '@vinted/monochrome-icons'
import { useState } from 'react'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import {
  deleteEprUin,
  EprUinModel,
} from '@marketplace-web/business-accounts/business-accounts-data'

import ConfirmationModal from '../ConfirmationModal'
import EditCountryUinModal from '../EditCountryUinModal'
import DropdownMenu, { DropdownActions } from '../../../components/DropdownMenu'

type Props = {
  uinList: Array<EprUinModel>
  countryTitle: string
  isListLoading: boolean
  refetch: () => void
}

const UinList = ({ uinList, countryTitle, isListLoading, refetch }: Props) => {
  const translate = useTranslate('epr_settings.uin_list')
  const breakpoints = useBreakpoint()
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isMobileEditActionsVisible, setIsMobileEditActionsVisible] = useState(false)
  const [showDeleteError, setShowDeleteError] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedUinNumber, setSelectedUinNumber] = useState('')
  const [selectedUinId, setSelectedUinId] = useState(0)

  const handleEditClick = () => {
    setIsEditModalVisible(true)
  }

  const handleEditClose = () => {
    setIsEditModalVisible(false)
  }

  const handleDeleteActionClick = () => {
    setIsDeleteModalVisible(true)
  }

  const handleDeleteConfirmClick = async () => {
    setIsLoading(true)

    const response = await deleteEprUin(selectedUinId)

    if ('errors' in response) {
      setIsLoading(false)
      setShowDeleteError(true)

      return
    }

    setIsLoading(false)

    refetch()

    setIsDeleteModalVisible(false)
  }

  const handleDeleteCancelClick = () => {
    setIsDeleteModalVisible(false)
  }

  const handleMobileEditActionsClose = () => {
    setIsMobileEditActionsVisible(false)
  }

  const handleMobileEditActionsOpen = () => {
    setIsMobileEditActionsVisible(true)
  }

  const renderMobileEditActions = (category: string) => (
    <BottomSheet
      isVisible={isMobileEditActionsVisible}
      onClose={handleMobileEditActionsClose}
      title={category}
      closeButtonEnabled
      closeOnOverlayClick
    >
      <div className="uin-edit-mobile">
        <Button
          text={translate('actions.edit')}
          styling="outlined"
          onClick={() => {
            handleMobileEditActionsClose()
            handleEditClick()
          }}
        />
        <Spacer size="medium" />
        <Button
          text={translate('actions.delete')}
          styling="outlined"
          theme="warning"
          onClick={() => {
            handleMobileEditActionsClose()
            handleDeleteActionClick()
          }}
        />
      </div>
    </BottomSheet>
  )

  const renderMobileEditActionsDots = (category: string, uin: string, id: number) => {
    return (
      <button
        type="button"
        onClick={() => {
          setSelectedCategory(category)
          setSelectedUinNumber(uin)
          setSelectedUinId(id)
          handleMobileEditActionsOpen()
        }}
      >
        <Icon
          testId="actions-menu-button"
          name={HorizontalDots24}
          color="greyscale-level-4"
          title={translate('a11y.show_more')}
        />
      </button>
    )
  }

  const renderExtraActions = (category: string, uin: string, id: number) => {
    const actions: Array<DropdownActions> = [
      {
        id: 'edit',
        text: translate('actions.edit'),
        onClick: () => {
          setSelectedCategory(category)
          setSelectedUinNumber(uin)
          setSelectedUinId(id)
          handleEditClick()
        },
      },
      {
        id: 'delete',
        text: translate('actions.delete'),
        onClick: () => {
          setSelectedUinId(id)
          handleDeleteActionClick()
        },
      },
    ]

    const dropdownButtonProps: ButtonProps = {
      styling: 'flat',
      icon: (
        <Icon
          testId="actions-menu-button"
          name={HorizontalDots24}
          color="greyscale-level-4"
          title={translate('a11y.show_more')}
        />
      ),
      size: 'medium',
    }

    return (
      <div>
        <DropdownMenu actions={actions} buttonProps={dropdownButtonProps} />
      </div>
    )
  }

  if (isListLoading) {
    return <ContentLoader />
  }

  return (
    <>
      <Label text={translate('title', { countryTitle })} />
      <Cell styling="tight">
        <SeparatedList separator={<Divider />}>
          {uinList[0]!.items.map(item => (
            <Cell
              title={item.category_title}
              body={item.uin}
              suffix={
                breakpoints.phones
                  ? renderMobileEditActionsDots(item.category_title, item.uin, item.id)
                  : renderExtraActions(item.category_title, item.uin, item.id)
              }
            />
          ))}
        </SeparatedList>
      </Cell>
      {isDeleteModalVisible && (
        <ConfirmationModal
          onConfirm={handleDeleteConfirmClick}
          onClose={handleDeleteCancelClick}
          isVisible={isDeleteModalVisible}
          isLoading={isLoading}
          title={translate('delete_uin_confirmation.title')}
          body={translate('delete_uin_confirmation.body')}
          confirmCtaText={translate('delete_uin_confirmation.confirm_cta')}
          cancelCtaText={translate('delete_uin_confirmation.cancel_cta')}
          confirmCtaTheme="warning"
          cancelCtaTheme="warning"
          showGeneralError={showDeleteError}
        />
      )}
      <EditCountryUinModal
        show={isEditModalVisible}
        category={selectedCategory}
        uin={selectedUinNumber}
        uinId={selectedUinId}
        onClose={handleEditClose}
        refetch={refetch}
      />
      {isMobileEditActionsVisible && renderMobileEditActions(selectedCategory)}
    </>
  )
}

export default UinList
